<template>
  <div>
    <div
      :class="[
        'tw-grid tw-grid-cols-2 tw-gap-4 apTablet3:tw-gap-6',
        'tw-justify-center tw-items-center apTablet3:tw-mb-[164px] tw-z-[10]',
        'apTablet3:tw-mt-[64px] tw-mt-[64px]'
      ]"
    >
      <div
        v-for="(card, index) in cards"
        :id="card.slug"
        :key="index"
        :class="[
          'tw-flex tw-flex-col tw-p-[32px_16px] apTablet3:tw-p-[32px_24px] tw-rounded-[16px] tw-group tw-relative',
          'apTablet3:tw-w-[304px] apTablet3:tw-h-[240px] tw-min-w-[163px] tw-w-full tw-h-[240px]',
          'dark:tw-bg-[#242424] tw-bg-gray-100 ',
          'hover:tw-cursor-pointer',
          'hover:tw-bg-gradient-to-b apTablet3:hover:tw-from-purple-600 apTablet3:hover:tw-to-pink-500 tw-duration-[1000ms] tw-transition-all'
        ]"
        @mouseover="isDesktop ? hoveredCard = index : null"
        @mouseleave="isDesktop ? hoveredCard = null : null"
        @click="onCardSelected(card)"
      >
        <!-- Top Left Icon -->
        <div class="tw-flex tw-flex-row tw-justify-between tw-items-center">
          <!-- Icon Logic -->
          <img
            :src="(hoveredCard === index || isDarkMode) ? card.iconDark : card.iconLight"
            alt="Icon"
            class="apDesktop:tw-w-10 apDesktop:tw-h-10 tw-w-8 tw-h-8 tw-flex-shrink-0 tw-transition-all"
          >

          <!-- Plus Icon Logic -->
          <img
            :src="$cdn(
              isDarkMode
                ? (isDesktop ? 'images/home/plus_icon.svg' : 'images/home/plus_icon_light.svg')
                : (isDesktop && hoveredCard === index
                  ? 'images/home/plus_icon.svg'
                  : 'images/home/plus_icon_light.svg')

            )"
            alt="Plus Icon"
            :class="[
              'apTablet3:tw-w-12 apTablet3:tw-h-12 tw-w-8 tw-h-8 tw-cursor-pointer tw-transition-all',
              hoveredCard !== index ? 'apTablet3:tw-opacity-0' : 'tw-opacity-100'
            ]"
          >
        </div>
        <!-- Bottom Text -->
        <div class="tw-mt-auto">
          <p
            :class="[
              'apTablet:tw-text-h3-bold-mobile tw-text-body-bold-mobile',
              'dark:tw-text-white tw-text-ap-nero tw-transition-colors',
              'apTablet3:hover:tw-text-gray-100',
              'tw-break-words tw-hyphens-auto',
              hoveredCard === index ? 'apTablet3:tw-text-white' : 'tw-text-ap-nero'
            ]"
          >
            {{ card.text }}
          </p>
        </div>
      </div>
    </div>

    <TeleportModal v-model:visible="modalCardGridOpened" :fit-content="true">
      <CardModalContent
        :text="selectedCard!.text"
        :description="selectedCard!.description"
        :tagline="selectedCard!.tagline"
        :slug="selectedCard!.slug"
      />
    </TeleportModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { watchDarkMode } from '../../support/helper';
import TeleportModal from '../shared/TeleportModal.vue';
import CardModalContent from './card-modal-content.vue';
import gsap from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
gsap.registerPlugin(ScrollToPlugin);
interface TempCard {
  iconLight: string;
  iconDark: string;
  text: string;
  tagline: string;
  description: string;
  hyperlink: string;
  slug: string;
}

const selectedCard = ref<TempCard | null>(null);
const hoveredCard = ref<number | null>(null);
const isDarkMode = ref(false);
let stopObserving: () => void;
const modalCardGridOpened = ref(false);

// Computed property to check if the screen is in desktop mode
const isDesktop = computed(() => window.innerWidth > 1024);

const onCardSelected = (card: TempCard) => {
  selectedCard.value = card;
  document.body.style.overflow = 'hidden';
  modalCardGridOpened.value = true;
};

const { cards } = defineProps({
  cards: {
    type: Array<TempCard>,
    required: true,
  },
});

function checkParams() {
  const params = new URLSearchParams(window.location.search);
  if (params.has('services')) {
    const services = params.get('services');
    scrollToSection('our-services', services);
    setTimeout(() => {
      if (services.length > 0) {
        const card = cards.find((card: TempCard) => card.slug === services);
        if (card) {
          onCardSelected(card);
        }
      }else{
        window.history.replaceState({}, '', '/services');
      }
    }, 1000);
  }
}

const scrollToSection = (section: string, subSection: string | null = null) => {
  const element = document.getElementById(section);
  const subElement = document.getElementById(subSection);

  if (element) {
    requestAnimationFrame(() => {
      const rect = element.getBoundingClientRect();
      const y = rect.top + window.scrollY;

      gsap.to(window, {
        scrollTo: { y },
        duration: 1,
        ease: 'power2.out',
        onComplete: () => {
          if (subElement) {
            subElement.scrollIntoView({ behavior: 'smooth' });
          }else{
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }
      });
    });
  }
};

onMounted(() => {
  stopObserving = watchDarkMode((value: any) => {
    isDarkMode.value = value;
  });
  window.addEventListener('load', checkParams);
});

onBeforeUnmount(() => {
  if (stopObserving) stopObserving();
});

</script>
